import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { parseNumberInput, getNumberInputValue } from '../utils/numberInputHelpers';

// Same limit as order_products.custom_name
const CUSTOM_NAME_MAX_LENGTH = 64;

let nextCustomKey = 1;

// CustomItemForm: the custom item button at the foot of a category's product
// list, and the small form it opens. Deliberately styled unlike a product row
// (dashed outline, no price, no Add button) so nobody mistakes it for a menu
// item. Submitting hands a preorder item flagged custom to onAdd, carrying the
// category it was opened from; send-items resolves it to that category's
// placeholder product.
function CustomItemForm({ theme, categoryId, onAdd }) {
    const { t } = useLanguage();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [qty, setQty] = useState(1);
    const [error, setError] = useState('');

    const reset = () => {
        setName('');
        setPrice('');
        setQty(1);
        setError('');
    };

    const handleCancel = () => {
        reset();
        setOpen(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (trimmed === '') {
            setError(t('customItemNameRequired'));
            return;
        }
        if (price === '' || !(Number(price) >= 0)) {
            setError(t('customItemPriceRequired'));
            return;
        }
        if (!Number.isInteger(qty) || qty < 1) {
            setError(t('customItemQuantityRequired'));
            return;
        }
        const roundedPrice = Math.round(Number(price) * 100) / 100;
        onAdd({
            // Local key only, so two custom lines never merge in the preorder
            // list. send-items replaces it with the placeholder's product id.
            id: `custom-${nextCustomKey++}`,
            custom: true,
            name: trimmed,
            price: roundedPrice,
            adjustedPrice: roundedPrice,
            qty,
            category_id: categoryId
        });
        reset();
        setOpen(false);
    };

    const inputStyle = {
        width: '100%',
        boxSizing: 'border-box',
        padding: '0.5rem',
        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
        borderRadius: theme?.borderRadius || 4,
        fontSize: '1rem',
        backgroundColor: theme?.colors?.surface || '#fff',
        color: theme?.colors?.text || '#000'
    };

    const labelStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        color: theme?.colors?.text || '#000',
        minWidth: 0
    };

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={t('customItemAdd')}
                style={{
                    width: '100%',
                    padding: '0.65rem 0.8rem',
                    marginTop: '0.25rem',
                    background: 'transparent',
                    color: theme?.colors?.textSecondary || '#666',
                    border: `2px dashed ${theme?.colors?.border || '#ccc'}`,
                    borderRadius: theme?.borderRadius || 6,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    fontStyle: 'italic',
                    textAlign: 'center'
                }}
            >
                + {t('customItem')}
            </button>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            aria-label={t('customItem')}
            style={{
                marginTop: '0.25rem',
                padding: '0.75rem',
                border: `2px dashed ${theme?.colors?.primary || '#1976d2'}`,
                borderRadius: theme?.borderRadius || 6,
                background: theme?.colors?.surface || '#fff',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
            }}
        >
            <div style={{ fontWeight: 'bold', color: theme?.colors?.primary || '#1976d2' }}>
                {t('customItem')}
            </div>
            <label style={labelStyle}>
                {t('customItemName')}
                <input
                    type="text"
                    value={name}
                    maxLength={CUSTOM_NAME_MAX_LENGTH}
                    autoFocus
                    onChange={(e) => { setName(e.target.value); setError(''); }}
                    placeholder={t('customItemNamePlaceholder')}
                    style={inputStyle}
                />
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <label style={{ ...labelStyle, flex: '2 1 120px' }}>
                    {t('customItemPrice')}
                    <input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={getNumberInputValue(price)}
                        onChange={(e) => { setPrice(parseNumberInput(e.target.value, { allowDecimals: true, min: 0 })); setError(''); }}
                        placeholder="0.00"
                        onWheel={(e) => e.target.blur()}
                        style={inputStyle}
                    />
                </label>
                <label style={{ ...labelStyle, flex: '1 1 80px' }}>
                    {t('customItemQuantity')}
                    <input
                        type="number"
                        inputMode="numeric"
                        value={getNumberInputValue(qty)}
                        onChange={(e) => { setQty(parseNumberInput(e.target.value, { min: 1 })); setError(''); }}
                        placeholder="1"
                        onWheel={(e) => e.target.blur()}
                        style={inputStyle}
                    />
                </label>
            </div>
            {error && (
                <div role="alert" style={{ color: theme?.colors?.error || '#d32f2f', fontSize: '0.85rem' }}>
                    {error}
                </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                    type="button"
                    onClick={handleCancel}
                    style={{
                        padding: '0.6rem 1rem',
                        background: theme?.colors?.secondary || '#888',
                        color: theme?.colors?.surface || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 6,
                        cursor: 'pointer',
                        fontWeight: 'bold'
                    }}
                >
                    {t('cancel')}
                </button>
                <button
                    type="submit"
                    style={{
                        padding: '0.6rem 1rem',
                        background: theme?.colors?.primary || '#1976d2',
                        color: theme?.colors?.surface || '#fff',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 6,
                        cursor: 'pointer',
                        fontWeight: 'bold'
                    }}
                >
                    {t('add')}
                </button>
            </div>
        </form>
    );
}

export default CustomItemForm;
